/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BuilderHelpModule } from '@gh/builder-help';
import { BuilderSharedModule } from '@gh/builder-shared';
import { BuilderSharedDataModule } from '@gh/builder-shared-data';
import {
  CommonInternalSharedModule,
  ConfirmationDialogComponent,
  GridSettingsDialogComponent,
  MessageDialogComponent,
} from '@gh/common-internal-shared';
import {
  ApplicationDialogHooks,
  CommonSharedModule,
  DEEP_LINK_FACTORY,
  HELP_SYSTEM,
} from '@gh/common-shared';

import { CommonSharedDataModule } from '@gh/common-shared-data';
import { defaultErrorHandler } from '@gh/common-shared/sentry';
import {
  AUTH_MODE,
  GOOGLE_CLIENT_ID,
  GOOGLE_DEFAULT_SCOPES,
  GOOGLE_HOSTED_DOMAIN,
  KEYCLOAK_AUTH_MODE,
  KEYCLOAK_WITH_MOCK_AUTH_MODE,
  MOCK_AUTH_MODE,
  MOCK_BACKEND_DEV_MODULE,
} from '@gh/config';
import { CoreDataModule, CoreMockDataModule } from '@gh/core-data';
import {
  CoreExternalCommonModule,
  CoreExternalGoogleModule,
  ExternalGoogleConfig,
} from '@gh/core-external';
import { CoreLazyModule } from '@gh/core-lazy';

import { CoreMessagesModule } from '@gh/core-messages';
import { CoreTypeModule } from '@gh/core-type';
import {
  CoreUiCommonModule,
  CoreUiDialogsModule,
  CoreUiFormModule,
  CoreUiMenuModule,
  CoreUiTableLayoutModule,
  CoreUiUploaderModule,
  CoreUiWaitingModule,
} from '@gh/core-ui';
import { CoreUIGridModule } from '@gh/core-ui-grid';

import { BuilderAppRoutingModule } from './builder-app-routing.module';

import {
  BuilderAppComponent,
  ColorSchemePickerComponent,
  JcDetailsDialogComponent,
  UserProfileSignatureBusinessService,
  UserProfileSignatureDialogComponent,
  UserProfileValidationDialogComponent,
} from './components';
import { LicenseAgreementDialogComponent } from './components/license-agreement-dialog/license-agreement-dialog.component';

import {
  BuilderAuthConfigModule,
  BuilderHelpSystem,
  BuilderHttpClientConfigModule,
  BuilderMlsConfigModule,
  LogoutHttpErrorInterceptor,
  BUILDER_PORTAL_DEEP_LINKS,
} from './config';

import { BuilderBootstrapService } from './config/builder-bootstrap.service';
import { BUILDER_MOCK_DATA } from './mock/builder-mock-data';
import { ColorSchemePickerBusinessService } from './resources/color-scheme-picker-business.service';
import { JcDetailsDialogBusinessService } from './resources/jc-details-dialog-business.service';
import { LicenseAgreementDialogBusinessService } from './resources/license-agreement-dialog-business.service';
import {
  UserProfilePhotoBusinessService,
  UserProfilePhotoDialogComponent,
} from './components/user-profile-photo';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { ToastrModule } from 'ngx-toastr';
import { MobileQrCodeDialogBusinessService } from './resources/mobile-qr-code-dialog-business.service';
import { MobileQrCodeDialogComponent } from './components/mobile-qr-code-dialog/mobile-qr-code-dialog.component';
import { MobileQrCodeDialogRestService } from './resources/mobile-qr-code-dialog-rest.service';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      closeButton: true,
      timeOut: 5000,
      extendedTimeOut: 5000,
      enableHtml: true,
    }),

    // core modules
    CoreMessagesModule,
    CoreUiWaitingModule.forRoot(),
    CoreUiFormModule.forRoot(),
    CoreUiDialogsModule.forRoot({
      dialogHooks: ApplicationDialogHooks,
      standardSet: {
        messageDialogType: MessageDialogComponent,
        confirmationDialogType: ConfirmationDialogComponent,
      },
    }),
    CoreUiUploaderModule.forRoot(),
    CoreUiTableLayoutModule.forRoot(),
    CoreUiMenuModule.forRoot(),
    CoreUiCommonModule.forRoot(),
    CoreLazyModule.forRoot(),
    CoreUIGridModule.forRoot({
      gridSettingsDialogType: GridSettingsDialogComponent,
    }),
    CoreDataModule.forRoot(),
    CoreTypeModule.forRoot(),
    CoreExternalCommonModule.forRoot(),
    CoreExternalGoogleModule.forRoot({
      client_id: GOOGLE_CLIENT_ID ?? '',
      scope: GOOGLE_DEFAULT_SCOPES ?? '',
      hosted_domain: GOOGLE_HOSTED_DOMAIN,
    } as ExternalGoogleConfig),

    // common modules
    CommonSharedDataModule.forRoot(),
    CommonSharedModule.forRoot(),
    CommonInternalSharedModule.forRoot(),

    // configuration modules
    (AUTH_MODE === KEYCLOAK_AUTH_MODE ? BuilderAuthConfigModule.useKeycloak() :
      AUTH_MODE === KEYCLOAK_WITH_MOCK_AUTH_MODE ? BuilderAuthConfigModule.useKeycloakWithMock() :
        AUTH_MODE === MOCK_AUTH_MODE ? BuilderAuthConfigModule.useMock() : BuilderAuthConfigModule.none()),
    BuilderHttpClientConfigModule,
    BuilderMlsConfigModule,

    // builder modules
    BuilderSharedDataModule.forRoot(),
    BuilderSharedModule.forRoot(),
    BuilderAppRoutingModule,

    BuilderHelpModule,

    MOCK_BACKEND_DEV_MODULE ? CoreMockDataModule.withMockData(BUILDER_MOCK_DATA) : [],
    ImageCropperComponent,

  ],
  providers: [
    defaultErrorHandler(),
    BuilderBootstrapService, {
      provide: APP_INITIALIZER,
      useFactory: (bootstap: BuilderBootstrapService) => () => bootstap.load(),
      deps: [BuilderBootstrapService],
      multi: true,
    },
    LogoutHttpErrorInterceptor,
    { provide: DEEP_LINK_FACTORY, useValue: BUILDER_PORTAL_DEEP_LINKS },
    { provide: HELP_SYSTEM, useClass: BuilderHelpSystem },
    UserProfileSignatureBusinessService,
    UserProfilePhotoBusinessService,
    ColorSchemePickerBusinessService,
    LicenseAgreementDialogBusinessService,
    JcDetailsDialogBusinessService,
    MobileQrCodeDialogBusinessService,
    MobileQrCodeDialogRestService,
  ],
  declarations: [
    BuilderAppComponent,
    UserProfileValidationDialogComponent,
    UserProfileSignatureDialogComponent,
    UserProfilePhotoDialogComponent,
    ColorSchemePickerComponent,
    LicenseAgreementDialogComponent,
    JcDetailsDialogComponent,
    MobileQrCodeDialogComponent,
  ],
  bootstrap: [BuilderAppComponent],
})
export class BuilderAppModule {
}
